import SlimSelect from 'slim-select';
import { checkParentClass } from '../../utilities/js/helper';

/**
 * Controller for native <select> elements using SlimSelect.
 *
 * Responsibilities:
 * - Determine opening direction based on viewport and configure SlimSelect accordingly
 * - Keep form field state classes in sync (is--open / is--filled)
 * - Special handling for language switchers and the contact phone field
 *
 * Public API: const api = new Select($select).initialize();
 */
class Select {
    /**
     * @param {HTMLSelectElement} element The native select element.
     */
    constructor (element) {
        /** @type {HTMLSelectElement} */
        this.$root = element;
        /** @type {SlimSelect|null} */
        this.slimSelect = null;

        this.initialize();
    }

    /**
     * Initialize the select: determines the opening direction and builds the SlimSelect instance.
     * Idempotent: safe to call multiple times.
     *
     * @returns {void}
     */
    initialize () {
        if (!this.$root) return;
        this.#maybeBuild();
    }

    /**
     * Compute the opening direction based on current viewport width.
     *
     * @returns {('up'|'down')} "up" for ≤ 1023px, otherwise "down".
     */
    _computeOpenDirection () {
        return (window.innerWidth <= 1023) ? 'up' : 'down';
    }

    /**
     * Build SlimSelect only when not opted-out via the technical exception class
     * ("technical-details-select").
     *
     * @returns {void}
     */
    #maybeBuild () {
        if (this.$root.classList.contains('technical-details-select')) return;
        const direction = this._computeOpenDirection();
        this._buildSlimSelect(this.$root, direction);
    }

    /**
     * Create the SlimSelect instance with context-appropriate options.
     *
     * @param {HTMLSelectElement} $select
     * @param {('up'|'down')} openDirection
     * @returns {void}
     */
    _buildSlimSelect ($select, openDirection) {
        /** @type {HTMLElement|null} */
        const $formField = this.$root.parentNode instanceof HTMLElement ? this.$root.parentNode : null;

        if ($select.classList.contains('lang-select')) {
            this.slimSelect = new SlimSelect({
                select: $select,
                showSearch: false,
                showContent: openDirection,
                onChange: (info) => {
                    const newPath = this._getNewPathFromChange(info);
                    if (newPath) window.location.href = newPath;
                }
            });
            return;
        }

        if (checkParentClass(this.$root, 'default-form')) {
            this.slimSelect = new SlimSelect({
                select: $select,
                showSearch: false,
                showContent: openDirection,
                onChange: (info) => {
                    const value = this._getValueFromChange(info);
                    if (!$formField) return;
                    if (value !== '') {
                        $formField.classList.add('is--filled');
                    } else {
                        $formField.classList.remove('is--filled');
                    }
                },
                afterOpen: () => {
                    $formField?.classList.add('is--open');
                },
                afterClose: () => {
                    $formField?.classList.remove('is--open');
                    if ($formField?.id === 'contact_phone') {
                        const $placeholder = $formField.querySelector('.placeholder');
                        this._setPhonePlaceholder($placeholder);
                    }
                }
            });

            if ($formField?.id === 'contact_phone') {
                const $placeholder = $formField.querySelector('.placeholder');
                this._setPhonePlaceholder($placeholder);
            }

            const sel = this.slimSelect?.selected ? this.slimSelect.selected() : (this.slimSelect?.getSelected ? this.slimSelect.getSelected() : null);
            const hasSelection = Array.isArray(sel) ? sel.length > 0 : (sel !== '' && sel != null);
            if (hasSelection) $formField?.classList.add('is--filled');
            return;
        }

        this.slimSelect = new SlimSelect({
            select: $select,
            showSearch: false,
            showContent: openDirection
        });
    }

    /**
     * Extract a target URL from the SlimSelect onChange callback payload.
     *
     * @param {any} info SlimSelect change payload.
     * @returns {string|undefined}
     */
    _getNewPathFromChange (info) {
        if (info && typeof info === 'object') {
            return info.value ?? info.data?.path ?? info.option?.dataset?.path;
        }
        return typeof info === 'string' ? info : undefined;
    }

    /**
     * Extract the (form) value from the SlimSelect onChange callback payload.
     *
     * @param {any} info SlimSelect change payload.
     * @returns {string|undefined}
     */
    _getValueFromChange (info) {
        if (info && typeof info === 'object') {
            return info.value ?? info.data?.value ?? info.option?.value;
        }
        return typeof info === 'string' ? info : undefined;
    }

    /**
     * Set the placeholder content for the contact phone field to a "+"-prefixed number format.
     *
     * @param {HTMLElement|null} placeholder Element that holds the placeholder text.
     * @returns {void}
     */
    _setPhonePlaceholder (placeholder) {
        if (!placeholder) return;
        const placeholderText = placeholder.innerHTML ?? '';
        const onlyNumbers = placeholderText.replace(/\D/g, '');
        const newPlaceholder = `+${onlyNumbers}`;
        placeholder.innerHTML = newPlaceholder;
    }
}

export { Select };

window.addEventListener('content.loaded', (e) => {
    const { $context } = e.detail ?? {};
    if (!$context) return;

    const $selects = $context.querySelectorAll('select');
    if (!$selects.length) return;

    $selects.forEach(($el) => {
        const selectAPI = new Select($el);
        // Historic behavior: expose API reference on the DOM element
        // @ts-ignore
        $el.API = selectAPI;
    });
});
